/**
 * NotificationStep — step 3 content (016 Onboarding Wizard, spec FR-009,
 * FR-010, US3).
 *
 * Two demo-stub toggle switches plus a visible demo-disclosure note. Both
 * toggles render with `role="switch"` and `aria-checked` mirroring local
 * state; clicking flips the visual state only — no permission API call,
 * no API request, no `SessionPort` write, no browser storage write. This
 * satisfies P7 (Explicit Transition) by being honest about the demo's
 * scope.
 *
 * GDPR opt-in pattern: both toggles default to `false`.
 *
 * Disclosure copy lives in the i18n bundle
 * (`onboarding.notifications.disclosure`).
 */

import { type JSX, useState } from 'react';

import { useTranslate } from '../../i18n/useTranslate.js';

const containerClasses = 'flex w-full max-w-onboarding-toggle mx-auto flex-col gap-4';
const toggleRowClasses = 'flex items-center justify-between gap-4 rounded-lg bg-muted p-3';
const toggleLabelClasses = 'text-sm font-medium text-foreground';
// Sizing is intentionally limited to the spacing tokens declared in
// `tokens/tokens.css` (1, 2, 3, 4, 5, 6, 8). `w-11` was the previous draft
// before that — `--spacing-11` is not declared in this project's scale so
// the class did not generate a width and the thumb sat past the pill edge.
//
// Geometry (all values resolve via declared tokens, no bracket classes):
//   pill  48 × 24   (w-8 / h-5)
//   thumb 16 × 16   (w-4 / h-4)
//   off:  translate-x-2  → x=8   (8 px left padding, 24 px to right edge)
//   on:   translate-x-5  → x=24  (24 px to left edge, 8 px right padding)
//
// Slide distance is 16 px; both states clear the pill edge by the same 8 px
// so the visual reads "thumb hugs left when off, hugs right when on" with
// matched breathing room rather than the thumb's right edge touching the
// pill on the on-state.
const switchBaseClasses =
  'relative inline-flex h-5 w-8 items-center rounded-full transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const switchOnClasses = 'bg-primary';
const switchOffClasses = 'bg-muted';
const thumbBaseClasses =
  'inline-block h-4 w-4 transform rounded-full bg-card shadow-sm transition-transform';
const thumbOnClasses = 'translate-x-5';
const thumbOffClasses = 'translate-x-2';
const disclosureClasses = 'rounded-md bg-warning-bg p-3 text-sm text-warning-fg';

interface ToggleSwitchProps {
  readonly checked: boolean;
  readonly label: string;
  readonly onToggle: () => void;
}

function ToggleSwitch({ checked, label, onToggle }: ToggleSwitchProps): JSX.Element {
  return (
    <div className={toggleRowClasses}>
      <span className={toggleLabelClasses}>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onToggle}
        className={`${switchBaseClasses} ${checked ? switchOnClasses : switchOffClasses}`}
      >
        <span
          className={`${thumbBaseClasses} ${checked ? thumbOnClasses : thumbOffClasses}`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

export function NotificationStep(): JSX.Element {
  const t = useTranslate();
  // GDPR opt-in: both toggles default to off (spec FR-009). Component-local
  // state only — no persistence, no permission API call (spec US3 #3 / #4).
  const [notifications, setNotifications] = useState(false);
  const [messages, setMessages] = useState(false);

  return (
    <div className={containerClasses}>
      <ToggleSwitch
        checked={notifications}
        label={t('onboarding.notifications.toggle.system')}
        onToggle={() => setNotifications((prev) => !prev)}
      />
      <ToggleSwitch
        checked={messages}
        label={t('onboarding.notifications.toggle.messages')}
        onToggle={() => setMessages((prev) => !prev)}
      />
      <aside role="note" className={disclosureClasses}>
        {t('onboarding.notifications.disclosure')}
      </aside>
    </div>
  );
}
